import { createAsyncThunk } from "@reduxjs/toolkit";
import { postData } from "../../../../apiservice/apis";
import dayjs from "dayjs";

export const fetchSalesOrderRecord = createAsyncThunk(
  "salesOrderRecord/fetch",
  async (
    { selectedFilterType, selectedRecordType, filters },
    { rejectWithValue }
  ) => {
    try {
      let url = "/sales/get-sales-order-record";
      if (selectedRecordType === "with item") {
        url = "/sales/get-sales-order-record-with-details";
      }
      if (
        selectedFilterType &&
        ["By Item", "By Tag", "By Group", "By Category"].includes(
          selectedFilterType
        )
      ) {
        url = "/sales/get-sales-order-details";
      }

      const payload = {
        selectedFilterType: selectedFilterType || null,
        selectedRecordType: selectedRecordType || null,
        locationId:
          selectedFilterType === "By Location"
            ? filters.locationId || null
            : null,
        customerId:
          selectedFilterType === "By Customer"
            ? filters.customerId || null
            : null,
        employeeId:
          selectedFilterType === "By Employee"
            ? filters.employeeId || null
            : null,
        itemId:
          selectedFilterType === "By Item" ? filters.itemId || null : null,
        groupId:
          selectedFilterType === "By Group" ? filters.groupId || null : null,
        categoryId:
          selectedFilterType === "By Category"
            ? filters.categoryId || null
            : null,
        userId:
          selectedFilterType === "By User" ? filters.userId || null : null,
        serialNumber:
          selectedFilterType === "By Item Serial"
            ? filters.serialNumber || ""
            : "",
        tag_groupnameid:
          selectedFilterType === "By Tag"
            ? filters.tag_groupnameid || null
            : null,
        tagFeildNameid:
          selectedFilterType === "By Tag"
            ? filters.tagFeildNameid || null
            : null,
        fromDate: filters.fromDate || null,
        toDate: filters.toDate || null,
      };

      const res = await postData(url, payload);

      let mapped = [];
      
  if (selectedRecordType === "with item") {
      mapped = res.data.map((saleOrder) => {
      const hasRemainingQty = saleOrder.details.some(
      (item) => Number(item.remaining_item_qty) > 0 
      );

      const hasDeliveredQty = saleOrder.details.some(
        (item) => Number(item.done_item_qty) > 0
      );
        
       return {
        // 🔑 One row per Sales Order
        id: saleOrder.sale_o_id,
        saleOrderId: saleOrder.sale_o_id,

        vch_no: saleOrder.sale_order_no,
        date: saleOrder.created_date,
        customer: saleOrder.acc_name,
        employee: saleOrder.employee_name,
        createdBy: saleOrder.full_name,
        mode: saleOrder.trans?.map((t) => t.to_acc_name).join(", ") || "",

        status: saleOrder.status === "c" ? "Approved" : "Pending",
        // Separate from the "status" mapping above (which means open/converted,
        // not approval state) — see approval_status on sales_order_master, set
        // by the discount-based approval workflow.
        approval_status: saleOrder.approval_status || "none",

        // ✅ Sales-order–level totals (shown in main row)
        total: Number(saleOrder.total_amount || 0).toFixed(2),
        subtotal: Number(saleOrder.sub_total || 0).toFixed(2),
        vat: Number(saleOrder.total_tax || 0).toFixed(2),
        discount: Number(saleOrder.total_discount || 0).toFixed(2),

        hasRemainingQty, 
        hasDeliveredQty,

        // ✅ Aggregates for summary row (optional)
        masterQty: saleOrder.details.reduce(
          (sum, i) => sum + Number(i.done_item_qty || 0),
          0
        ),
        baseQty: saleOrder.details.reduce(
          (sum, i) => sum + Number(i.done_retail_qty || 0),
          0
        ),

      // 🔽 Item details go here (expandable)
      items: saleOrder.details.map((item, idx) => ({
        id: `${saleOrder.sale_o_id}-${item.item_id}-${idx}`,
        item: item.item_name,
        rate: Number(item.item_rate || 0).toFixed(2),
        discount: item.item_discount_per ? `${item.item_discount_per}%` : "",
        vat: item.item_tax_per ? `${item.item_tax_per}%` : "",
        // Sold / Ordered
        qty: `${item.done_item_qty}/${item.item_qty} ${item.unit_symbol || ""}`,
        remainingQty: Number(item.remaining_item_qty || 0),
        total: Number(item.item_total || 0).toFixed(2),
        masterQty: Number(item.done_item_qty || 0),
        baseQty: Number(item.done_retail_qty || 0),
        unitDetails: `Sold: ${item.done_item_qty} ${
        item.unit_symbol
        }, Ordered: ${item.item_qty} ${item.unit_symbol}`,
      })),
    }});
  } else {
        // Normal summary view
        mapped = res.data.map((row) => ({
          id: row.sale_o_id,
          vch_no: row.sale_order_no,
          date: row.created_date,
          customer: row.acc_name,
          employee: row.employee_name,
          mode: row.trans?.map((t) => t.to_acc_name).join(", ") || "",
          subtotal: Number(row.sub_total || 0).toFixed(2),
          discount: Number(row.total_discount || 0).toFixed(2),
          vat: Number(row.total_tax || 0).toFixed(2),
          service: Number(row.other_service_cost || 0).toFixed(2),
          total: Number(row.total_amount || 0).toFixed(2),
          paid: Number(row.paid_total || 0).toFixed(2),
          due: Number(row.due_total || 0).toFixed(2),
          status: row.status === "c" ? "Approved" : "Pending",
          // Separate from the "status" mapping above (which means open/converted,
          // not approval state) — see approval_status on sales_order_master, set
          // by the discount-based approval workflow.
          approval_status: row.approval_status || "none",
        }));
      }

      return mapped;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteSalesOrder = createAsyncThunk(
  "salesOrderRecord/delete",
  async (saleOrderId, { rejectWithValue }) => {
    try {
      const res = await postData("/sales/sales-order-delete", {
        sale_o_id: saleOrderId,
      });

      if (res?.error) return rejectWithValue(res.error);
      return saleOrderId; // return id for optimistic update
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);


